import {
  createContext,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';
import {
  type EditSessionMethods,
  useEditSession,
  type UseEditSessionReturn,
} from './useEditSession';

type EditSessionContextValue = Omit<UseEditSessionReturn, 'isPending'>;

const EditSessionContext = createContext<EditSessionContextValue>(undefined!);
const EditSessionPendingContext = createContext(false);

export const useEditSessionContext = () => useContext(EditSessionContext);

export const useEditSessionPending = () =>
  useContext(EditSessionPendingContext);

export interface EditSessionContextProviderProps
  extends EditSessionMethods, PropsWithChildren {
  sessionId: string | undefined;
  onSessionClose: (reopen: boolean) => unknown;
  refreshRows: () => unknown;
  reloadAfterInsert?: boolean;
}

export const EditSessionContextProvider: FC<
  EditSessionContextProviderProps
> = ({ children, ...props }) => {
  const {
    sessionId,
    updateRow,
    insertRows,
    insertRow,
    insertExcelRows,
    deleteRow,
    deleteRows,
    validateRow,
    commitEdits,
    flushEdits,
    registerCommitPreparation,
    discardEdits,
    resetSession,
    refreshRows,
    isPending,
  } = useEditSession(props);

  const value = useMemo(
    () => ({
      sessionId,
      updateRow,
      insertRows,
      insertRow,
      insertExcelRows,
      deleteRow,
      deleteRows,
      validateRow,
      commitEdits,
      flushEdits,
      registerCommitPreparation,
      discardEdits,
      resetSession,
      refreshRows,
    }),
    [
      sessionId,
      updateRow,
      insertRows,
      insertRow,
      insertExcelRows,
      deleteRow,
      deleteRows,
      validateRow,
      commitEdits,
      flushEdits,
      registerCommitPreparation,
      discardEdits,
      resetSession,
      refreshRows,
    ],
  );

  return (
    <EditSessionContext.Provider value={value}>
      <EditSessionPendingContext.Provider value={isPending}>
        {children}
      </EditSessionPendingContext.Provider>
    </EditSessionContext.Provider>
  );
};
